import * as most from 'most';
import type { Stream } from 'most';
import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { SubscribeStream } from 'common/components/combinators/SubscribeStream';

type Props<V> = {
  value: V;
  delay: number;
  children: (value: V) => ReactNode;
};

export function Delayed<V>({ value, delay, children }: Props<V>): ReactElement {
  const [observedValue, setObservedValue] = useState<Stream<V>>(() =>
    most.of(value)
  );
  // The previous value is held in state rather than a ref so that recognising
  // a change stays a pure function of this render. React renders a component
  // more than once per commit -- StrictMode does so on every render in
  // development -- and a ref written here would already carry the new value on
  // the second pass, so the comparison would come out false and the stream
  // would never be replaced.
  //
  // Both halves wrap `value` in a thunk because `V` is unconstrained: a
  // function value handed to `useState` or a setter directly is taken for a
  // lazy initialiser or an updater and called, storing its return value
  // instead of the value itself.
  const [previousValue, setPreviousValue] = useState<V>(() => value);

  if (previousValue !== value) {
    setPreviousValue(() => value);
    setObservedValue(most.of(value).delay(delay));
  }

  return <SubscribeStream stream={observedValue}>{children}</SubscribeStream>;
}
